import {Injectable} from '@angular/core';
import {LocalStorageService} from 'ngx-webstorage';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {IUser} from '../interfaces/user.interface';
import {StorageUrlService} from './storage-url.service';

/** Uitkomst van de eerste inlogstap. */
export interface ILoginResult {
  requiresTwoFactor: boolean;
  challenge?: string;
  /** "authenticator" en/of "email". */
  methods?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public set token(value: string) {
    this.localStorage.store('token', value);
  }

  public get token(): string {
    return this.localStorage.retrieve('token') ?? null;
  }

  public set user(value: IUser) {
    this.localStorage.store('user', value);
  }

  public get user(): IUser {
    return this.localStorage.retrieve('user') ?? null;
  }

  /** "Dit apparaat onthouden"-token — slaat de 2FA-codestap 30 dagen over
   *  op deze browser. Los van `token`: overleeft een uitloggen bewust, want
   *  dat is nou juist het hele punt (opnieuw inloggen zonder codestap). */
  public set deviceToken(value: string) {
    this.localStorage.store('deviceToken', value);
  }

  public get deviceToken(): string {
    return this.localStorage.retrieve('deviceToken') ?? null;
  }

  constructor(private localStorage: LocalStorageService,
              private httpClient: HttpClient,
              private storageUrlService: StorageUrlService) {
  }


  /**
   * Inloggen met e-mailadres en wachtwoord.
   *
   * Heeft deze gebruiker een tweede stap aanstaan, dan komt er geen token maar
   * een kortlevend bewijs terug en vraagt het scherm om een code.
   */
  public login(data: {email, password}): Observable<ILoginResult> {
    // Is dit toestel al onthouden, dan stuurt het token mee — heeft de
    // gebruiker 2FA aan staan, dan slaat het antwoord de codestap voor ons
    // over zolang het token nog geldig is.
    const body = this.deviceToken ? { ...data, deviceToken: this.deviceToken } : data;
    return this.httpClient.post<any>(`${environment.authUrl}/Account/Login`, body)
      .pipe(
        map((res) => {
          if (res?.requiresTwoFactor) {
            return {
              requiresTwoFactor: true,
              challenge: res.challenge,
              methods: res.twoFactorMethods ?? []
            } as ILoginResult;
          }
          this.bewaarSessie(res.token);
          return { requiresTwoFactor: false } as ILoginResult;
        })
      );
  }

  /** De tweede stap: code uit de app, uit de e-mail, of een herstelcode.
   *  `rememberDevice` vraagt om een nieuw apparaat-token terug, zodat deze
   *  browser de codestap de eerstvolgende 30 dagen mag overslaan. */
  public loginTwoFactor(challenge: string, code: string, rememberDevice: boolean): Observable<void> {
    return this.httpClient.post<any>(`${environment.authUrl}/Account/Login/TwoFactor`, { challenge, code, rememberDevice })
      .pipe(map((res) => {
        if (res.deviceToken) this.deviceToken = res.deviceToken;
        this.bewaarSessie(res.token);
      }));
  }

  /** Vraagt om een code per e-mail, halverwege het inloggen. */
  public sendTwoFactorEmail(challenge: string): Observable<unknown> {
    return this.httpClient.post(`${environment.authUrl}/Account/Login/TwoFactor/SendEmail`, { challenge });
  }

  private bewaarSessie(token: string): void {
    this.token = token;
    this.user = this.parseJwt(token);
    // Zonder dit bewijs blijven logo's, video's en miniaturen leeg tot de
    // volgende paginaherlaad — /storage/ eist het sinds 20-08-2026.
    this.storageUrlService.refresh();
  }

  public logout(): void {
    this.localStorage.clear('token');
    this.localStorage.clear('user');
    this.storageUrlService.clear();
  }

  /**
   * Is de vervaldatum in het token voorbij? Een token zonder `exp` (van vóór
   * de levensduur van zeven dagen) geldt als niet verlopen; de dienst zelf
   * heeft dan het laatste woord.
   */
  public tokenVerlopen(): boolean {
    const token = this.token;
    if (!token) {
      return true;
    }
    try {
      const data = JSON.parse(atob(token.split('.')[1]));
      return typeof data.exp === 'number' && data.exp * 1000 <= Date.now();
    } catch {
      return false;
    }
  }

  private parseJwt(token: string): IUser {
    try {
      const data = JSON.parse(atob(token.split('.')[1]));
      return  {
        sid: data.nameid,
        activated: true,
        club: undefined,
        email: data.email,
        firstName: data.given_name,
        lastName: data.family_name,
        role: data.role,
        languageKey: data.language,
        phone: null,
        teams: []
      };
    } catch (e) {
      return null;
    }
  }
}
